import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Expand } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { Reveal, Eyebrow } from "./Reveal";

export const Gallery = () => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(null);

  const photos = [
    { src: "/images/hero.png", title: t.cap1_title, sub: t.cap1_sub, testid: "gallery-item-hero-aerial" },
    { src: "/images/villa-aerial.png", title: t.cap2_title, sub: t.cap2_sub, testid: "gallery-item-single-villa" },
    { src: "/images/villa-gate.png", title: t.cap3_title, sub: t.cap3_sub, testid: "gallery-item-villa-gate" },
  ];

  return (
    <section id="villas" data-testid="gallery-section" className="relative py-28 sm:py-36 bg-[#050505]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <Eyebrow>{t.gallery_eyebrow}</Eyebrow>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#F3E5AB] max-w-2xl leading-tight">
            {t.gallery_title}
          </h2>
          <p className="mt-5 max-w-xl text-[#9C9488] font-light leading-relaxed">{t.gallery_sub}</p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Reveal className="lg:row-span-2">
            <GalleryCard photo={photos[0]} onOpen={() => setOpen(0)} tall />
          </Reveal>
          <Reveal delay={0.12}>
            <GalleryCard photo={photos[1]} onOpen={() => setOpen(1)} />
          </Reveal>
          <Reveal delay={0.22}>
            <GalleryCard photo={photos[2]} onOpen={() => setOpen(2)} />
          </Reveal>
        </div>
      </div>

      <AnimatePresence>
        {open !== null && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 sm:p-10"
            onClick={() => setOpen(null)}
          >
            <button
              data-testid="gallery-lightbox-close"
              onClick={() => setOpen(null)}
              className="absolute top-6 end-6 text-[#EED8A1] p-2 border border-[rgba(197,160,89,0.4)] hover:bg-[rgba(197,160,89,0.12)] transition-colors"
              aria-label={t.popup_close}
            >
              <X size={20} />
            </button>
            <motion.figure
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-5xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={photos[open].src}
                alt={photos[open].title}
                className="w-full max-h-[78vh] object-contain border border-[rgba(197,160,89,0.3)]"
              />
              <figcaption className="mt-4 text-center">
                <span className="block font-serif text-xl text-[#F3E5AB]">{photos[open].title}</span>
                <span className="block mt-1 text-xs tracking-[0.2em] uppercase text-[#8A8270]">{photos[open].sub}</span>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

const GalleryCard = ({ photo, onOpen, tall }) => (
  <button
    data-testid={photo.testid}
    onClick={onOpen}
    className={`group relative block w-full overflow-hidden border border-[rgba(197,160,89,0.22)] text-start ${
      tall ? "h-[420px] lg:h-full min-h-[420px]" : "h-[300px] sm:h-[340px]"
    }`}
  >
    <img
      src={photo.src}
      alt={photo.title}
      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.06]"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />
    <div className="absolute inset-3 border border-[rgba(197,160,89,0)] group-hover:border-[rgba(197,160,89,0.45)] transition-all duration-700 pointer-events-none" />
    <div className="absolute bottom-0 inset-x-0 p-6">
      <span className="block font-serif text-xl sm:text-2xl text-[#F3E5AB]">{photo.title}</span>
      <span className="block mt-1 text-[11px] tracking-[0.22em] uppercase text-[#C7BFA7]/80">{photo.sub}</span>
    </div>
    <div className="absolute top-5 end-5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 text-[#EED8A1]">
      <Expand size={18} />
    </div>
  </button>
);
